import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';

// Decode JWT utility function (same as in AssignTickets)
const decodeToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload;
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

// Date utility functions (adapted from AssignTickets)
const formatDate = (dateString) => {
  if (!dateString) return "Not assigned";
  try {
    const date = new Date(dateString);
    if (isNaN(date)) return "Invalid date";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return "Invalid date";
  }
};

const getDateRange = (range) => {
  const today = new Date();
  const start = new Date();
  
  switch (range) {
    case "today":
      start.setHours(0, 0, 0, 0);
      return { start: start.toISOString(), end: today.toISOString() };
    case "weekly":
      start.setDate(today.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return { start: start.toISOString(), end: today.toISOString() };
    case "monthly":
      start.setMonth(today.getMonth() - 1);
      start.setHours(0, 0, 0, 0);
      return { start: start.toISOString(), end: today.toISOString() };
    default:
      return { start: null, end: null };
  }
};

// StatusBadge and PriorityBadge (copied from AssignTickets for consistency)
const StatusBadge = ({ status }) => {
  const colors = {
    open: "bg-blue-100 text-blue-800",
    inprogress: "bg-yellow-100 text-yellow-800",
    resolved: "bg-green-100 text-green-800",
    closed: "bg-gray-300 text-gray-700",
    pending: "bg-orange-100 text-orange-800",
    reopen: "bg-blue-100 text-blue-800",
    assigned: "bg-green-100 text-green-800",
    reassigned: "bg-yellow-100 text-yellow-800",
  };
  const colorClass = colors[status?.toLowerCase().replace(/\s/g, "")] || "bg-gray-100 text-gray-800";
  
  const statusInfo = {
    open: { icon: "🔓", text: "Open" },
    inprogress: { icon: "🔄", text: "In Progress" },
    resolved: { icon: "✅", text: "Resolved" },
    closed: { icon: "🔒", text: "Closed" },
    pending: { icon: "⏳", text: "Pending" },
    reopen: { icon: "🔓", text: "Reopened" },
    assigned: { icon: "✅", text: "Assigned" },
    reassigned: { icon: "🔄", text: "Reassigned" },
  };
  const info = statusInfo[status?.toLowerCase().replace(/\s/g, "")] || { icon: "", text: status || "Unknown" };
  
  return (
    <motion.span 
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.3 }}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${colorClass}`}
    >
      <motion.span
        animate={{ rotate: [0, 10, -10, 0] }}
        transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 5 }}
        className="mr-1"
      >
        {info.icon}
      </motion.span>
      {info.text}
    </motion.span>
  );
};

const PriorityBadge = ({ priority }) => {
  const colors = {
    high: "bg-red-100 text-red-800",
    medium: "bg-yellow-100 text-yellow-800",
    low: "bg-green-100 text-green-800",
  };
  const colorClass = colors[priority?.toLowerCase()] || "bg-gray-100 text-gray-800";
  
  return (
    <motion.span 
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${colorClass}`}
    >
      <motion.span
        animate={{ 
          scale: priority === "high" ? [1, 1.2, 1] : 1,
        }}
        transition={{ duration: 0.5, repeat: priority === "high" ? Infinity : 0, repeatDelay: 2 }}
        className="mr-1"
      >
        {priority === "high" && "🔥"}
        {priority === "medium" && "⚠️"}
        {priority === "low" && "💤"}
      </motion.span>
      {priority ? `${priority} Priority` : "No Priority"}
    </motion.span>
  );
};

// Custom hook for dashboard data (adapted from AssignTickets fetch logic)
const useDashboardData = () => {
  const [data, setData] = useState({
    tickets: [],
    employees: [],
    departments: [],
    stats: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("Access token not found.");

        const decodedToken = decodeToken(token);
        const companyId = decodedToken?.companyId || decodedToken?.id;

        if (!companyId) throw new Error("Company ID not found in token");

        console.log("Dashboard - Company ID from token:", companyId); // Debug

        const [empRes, deptRes, ticketRes] = await Promise.all([
          fetch("https://customersupport-jhy9.onrender.com/api/employees", { 
            headers: { Authorization: `Bearer ${token}` } 
          }),
          fetch(`https://customersupport-jhy9.onrender.com/api/departments/company/${companyId}`, { 
            headers: { Authorization: `Bearer ${token}` } 
          }),
          fetch("https://customersupport-jhy9.onrender.com/api/support-ticket", { 
            headers: { Authorization: `Bearer ${token}` } 
          }),
        ]);

        if (!empRes.ok || !deptRes.ok || !ticketRes.ok) {
          throw new Error("Failed to fetch dashboard data");
        }

        let empData = await empRes.json();
        const deptData = await deptRes.json();
        const ticketData = await ticketRes.json();
        const allTickets = ticketData.tickets || ticketData || [];

        // Filter employees by company
        empData = empData.filter((emp) => emp.company === companyId);

        const empMap = {};
        const empNameMap = {};
        empData.forEach((emp) => {
          empMap[emp._id] = emp;
          empNameMap[emp._id] = emp.teamMemberName;
        });

        const deptMap = {};
        const deptIdToName = {};
        deptData.forEach((dept) => {
          deptMap[dept._id] = dept;
          deptIdToName[dept._id] = dept.name;
        });

        // Enhance and filter tickets (same logic as AssignTickets)
        const enhancedTickets = allTickets
          .map((ticket) => {
            const employee = empMap[ticket.customerId];

            const assignedEmployeeName = ticket.assignedTo
              ? empNameMap[ticket.assignedTo] || "Unknown Employee"
              : null;

            const departmentId = employee ? employee.department : null;
            const departmentName = departmentId ? deptIdToName[departmentId] || "Unknown" : "Company";

            return {
              ...ticket,
              employeeName: employee ? employee.teamMemberName : "Company",
              departmentId,
              department: departmentName,
              company: ticket.company || companyId,
              assignedEmployee: ticket.assignedEmployee || "",
              assignedTo: ticket.assignedTo || null,
              assignedEmployeeName,
              assignedDate: ticket.assignedDate || null,
              assignedBy: ticket.assignedBy || null,
              reassignDescription: ticket.reassignDescription || "",
              reassignedTo: ticket.reassignedTo || null,
              reassignedBy: ticket.reassignedBy || null,
              reassignedDate: ticket.reassignedDate || null,
              updatedAt: ticket.updatedAt || null,
              createdAt: ticket.createdAt || null,
              fieldWorkAssigned: ticket.fieldWorkAssigned || false,
              isAssigned: !!ticket.assignedTo,
              material: ticket.material || "",
              service: ticket.service || "",
              location: ticket.location || "",
              reopenReason: ticket.reopenReason || "",
              // Resolution time for closed/resolved
              resolutionTime: (ticket.status === 'closed' || ticket.status === 'resolved') ? 
                Math.round((new Date(ticket.updatedAt) - new Date(ticket.createdAt)) / (1000 * 60 * 60)) : null // Hours
            };
          })
          .filter(
            (t) =>
              t.company === companyId && (empMap[t.customerId] || t.customerId === companyId)
          )
          .sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime() || 0;
            const dateB = new Date(b.createdAt).getTime() || 0;
            return dateB - dateA; // Newest first
          });

        // Pre-calculate stats
        const totalTickets = enhancedTickets.length;
        const openTickets = enhancedTickets.filter(t => ['open', 'pending'].includes(t.status?.toLowerCase())).length;
        const inProgressTickets = enhancedTickets.filter(t => t.status?.toLowerCase() === 'in progress').length;
        const resolvedTickets = enhancedTickets.filter(t => t.status?.toLowerCase() === 'resolved').length;
        const closedTickets = enhancedTickets.filter(t => t.status?.toLowerCase() === 'closed').length;
        const assignedTickets = enhancedTickets.filter(t => t.isAssigned).length;
        const unassignedTickets = totalTickets - assignedTickets;
        const avgRating = 0; // Placeholder; fetch reviews if needed
        const avgResolutionTime = (resolvedTickets + closedTickets > 0) 
          ? Math.round(
              enhancedTickets
                .filter(t => t.resolutionTime)
                .reduce((sum, t) => sum + t.resolutionTime, 0) / (resolvedTickets + closedTickets)
            ) 
          : 0;

        setData({
          tickets: enhancedTickets,
          employees: empData,
          departments: deptData,
          stats: {
            totalTickets,
            openTickets,
            inProgressTickets,
            resolvedTickets,
            closedTickets,
            assignedTickets,
            unassignedTickets,
            avgRating,
            avgResolutionTime
          }
        });
      } catch (err) {
        console.error("Dashboard data fetch error:", err);
        setError(err.message || "Error loading dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return { data, loading, error };
};

// Stats Cards Component (adapted with statuses from AssignTickets)
const StatsCards = ({ stats }) => (
  <motion.div 
    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, staggerChildren: 0.1 }}
  >
    <motion.div 
      className="bg-white p-6 rounded-2xl shadow-md border border-gray-200"
      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">Total Tickets</p>
          <p className="text-3xl font-bold text-gray-900">{stats.totalTickets}</p>
        </div>
        <div className="p-3 rounded-full bg-blue-100">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
      </div>
    </motion.div>

    <motion.div 
      className="bg-white p-6 rounded-2xl shadow-md border border-gray-200"
      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">Open/Pending</p>
          <p className="text-3xl font-bold text-red-600">{stats.openTickets}</p>
        </div>
        <div className="p-3 rounded-full bg-red-100">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>
    </motion.div>

    <motion.div 
      className="bg-white p-6 rounded-2xl shadow-md border border-gray-200"
      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">In Progress</p>
          <p className="text-3xl font-bold text-yellow-600">{stats.inProgressTickets}</p>
        </div>
        <div className="p-3 rounded-full bg-yellow-100">
          <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>
    </motion.div>

    <motion.div 
      className="bg-white p-6 rounded-2xl shadow-md border border-gray-200"
      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">Resolved</p>
          <p className="text-3xl font-bold text-green-600">{stats.resolvedTickets}</p>
        </div>
        <div className="p-3 rounded-full bg-green-100">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      </div>
    </motion.div>

    <motion.div 
      className="bg-white p-6 rounded-2xl shadow-md border border-gray-200"
      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">Closed</p>
          <p className="text-3xl font-bold text-teal-600">{stats.closedTickets}</p>
        </div>
        <div className="p-3 rounded-full bg-teal-100">
          <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>
    </motion.div>

    <motion.div 
      className="bg-white p-6 rounded-2xl shadow-md border border-gray-200"
      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">Assigned Tickets</p>
          <p className="text-3xl font-bold text-indigo-600">{stats.assignedTickets}</p>
        </div>
        <div className="p-3 rounded-full bg-indigo-100">
          <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
      </div>
    </motion.div>

    <motion.div 
      className="bg-white p-6 rounded-2xl shadow-md border border-gray-200"
      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">Avg Resolution Time</p>
          <p className="text-3xl font-bold text-purple-600">{stats.avgResolutionTime}h</p>
        </div>
        <div className="p-3 rounded-full bg-purple-100">
          <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

// Charts Section (adapted with statuses from AssignTickets)
const DashboardCharts = ({ tickets, employees, departments }) => {
  // Status distribution for Pie Chart (using AssignTickets statuses)
  const statusData = useMemo(() => {
    const distribution = tickets.reduce((acc, ticket) => {
      const status = ticket.status?.toLowerCase() || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(distribution).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1).replace('in progress', 'In Progress').replace('reopen', 'Reopen'),
      value
    }));
  }, [tickets]);

  // Trend data for Line Chart (last 30 days, using createdAt)
  const trendData = useMemo(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const dailyTickets = tickets
      .filter(t => new Date(t.createdAt) >= thirtyDaysAgo)
      .reduce((acc, ticket) => {
        const date = new Date(ticket.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {});

    const dates = [];
    for (let i = 0; i < 30; i++) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      dates.unshift({ date: formattedDate, tickets: dailyTickets[formattedDate] || 0 });
    }
    return dates;
  }, [tickets]);

  // Top employees for Bar Chart (top 5 by resolved/closed tickets)
  const topEmployeesData = useMemo(() => {
    const empPerformance = employees.map(emp => {
      const empTickets = tickets.filter(t => t.assignedTo === emp._id && ['resolved', 'closed'].includes(t.status?.toLowerCase()));
      return {
        name: emp.teamMemberName || emp.name,
        completed: empTickets.length,
        department: emp.departmentName || 'Unknown'
      };
    }).sort((a, b) => b.completed - a.completed).slice(0, 5);
    return empPerformance;
  }, [tickets, employees]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  return (
    <motion.div 
      className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Status Distribution Pie Chart */}
      <motion.div 
        className="bg-white p-6 rounded-2xl shadow-md border border-gray-200"
        whileHover={{ y: -2 }}
        transition={{ duration: 0.2 }}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Ticket Status Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={statusData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {statusData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Tickets Trend Line Chart */}
      <motion.div 
        className="bg-white p-6 rounded-2xl shadow-md border border-gray-200"
        whileHover={{ y: -2 }}
        transition={{ duration: 0.2 }}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Tickets Created (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" angle={-45} textAnchor="end" height={60} />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="tickets" stroke="#3182ce" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Top Employees Bar Chart */}
      <motion.div 
        className="bg-white p-6 rounded-2xl shadow-md border border-gray-200 lg:col-span-2"
        whileHover={{ y: -2 }}
        transition={{ duration: 0.2 }}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Employees by Resolved Tickets</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={topEmployeesData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
            <YAxis />
            <Tooltip />
            <Bar dataKey="completed" fill="#10b981" />
            <Legend />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>
    </motion.div>
  );
};

// Recent Tickets Table (using StatusBadge and PriorityBadge from AssignTickets)
const RecentTicketsTable = ({ tickets, employees }) => {
  // Sort by createdAt (recent first)
  const recentTickets = useMemo(() => 
    tickets
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 10), // Last 10
    [tickets]
  );

  // Get employee name by ID
  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e._id === empId);
    return emp ? emp.teamMemberName || emp.name : 'Unassigned';
  };

  return (
    <motion.div 
      className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">Recent Tickets</h3>
        <p className="text-sm text-gray-600 mt-1">Last 10 tickets (sorted by creation date)</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <AnimatePresence>
              {recentTickets.map((ticket, index) => (
                <motion.tr 
                  key={ticket._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{ticket._id?.substring(0, 8) || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                    {ticket.subject || 'No subject'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={ticket.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <PriorityBadge priority={ticket.priority} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {ticket.department || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getEmployeeName(ticket.assignedTo)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(ticket.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        View
                      </motion.button>
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        className="text-green-600 hover:text-green-900"
                      >
                        Assign
                      </motion.button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
      {recentTickets.length === 0 && (
        <motion.div 
          className="p-6 text-center text-gray-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          No recent tickets available.
        </motion.div>
      )}
    </motion.div>
  );
};

// Simple Filters Component (adapted from AssignTickets)
const DashboardFilters = ({ filters, onFilterChange }) => {
  return (
    <motion.div 
      className="bg-gradient-to-br from-white to-blue-50/50 rounded-3xl p-6 shadow-xl border border-blue-100/50 mb-8 backdrop-blur-sm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-blue-100">
        <div className="flex items-center">
          <svg className="w-6 h-6 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <h3 className="text-xl font-bold text-gray-800">Filter Dashboard</h3>
        </div>
        <motion.button
          onClick={() => onFilterChange('reset', null)}
          whileHover={{ scale: 1.05 }}
          className="text-sm bg-red-100 hover:bg-red-200 text-red-700 font-semibold px-4 py-2 rounded-full flex items-center"
        >
          Clear All
        </motion.button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Date Range */}
        <motion.div whileHover={{ y: -2 }} className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
          <select
            value={filters.dateRange || ''}
            onChange={(e) => onFilterChange('dateRange', e.target.value)}
            className="w-full border-2 border-gray-200 rounded-2xl p-3 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          >
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="weekly">Last 7 Days</option>
            <option value="monthly">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>
        </motion.div>

        {/* Status Filter */}
        <motion.div whileHover={{ y: -2 }} className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select
            value={filters.status || ''}
            onChange={(e) => onFilterChange('status', e.target.value)}
            className="w-full border-2 border-gray-200 rounded-2xl p-3 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="in progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
            <option value="pending">Pending</option>
            <option value="reopen">Reopen</option>
            <option value="assigned">Assigned</option>
            <option value="reassigned">Reassigned</option>
          </select>
        </motion.div>

                {/* Custom Date Inputs */}
        {filters.dateRange === 'custom' && (
          <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <motion.div whileHover={{ y: -2 }}>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => onFilterChange('startDate', e.target.value)}
                className="w-full border-2 border-gray-200 rounded-2xl p-3 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </motion.div>
            <motion.div whileHover={{ y: -2 }}>
              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => onFilterChange('endDate', e.target.value)}
                className="w-full border-2 border-gray-200 rounded-2xl p-3 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </motion.div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Export Component (Simple CSV Export, adapted from previous)
const ExportDashboard = ({ tickets }) => {
  const exportToCSV = () => {
    const headers = ['ID', 'Subject', 'Status', 'Priority', 'Department', 'Assigned To', 'Created At', 'Customer Name', 'Service', 'Material', 'Location'];
    const csvContent = [
      headers.join(','),
      ...tickets.map(ticket => [
        ticket._id?.substring(0, 8) || '',
        `"${ticket.subject || ''}"`,
        ticket.status || '',
        ticket.priority || '',
        `"${ticket.department || ''}"`,
        `"${ticket.assignedEmployeeName || 'Unassigned'}"`,
        formatDate(ticket.createdAt),
        `"${ticket.customerName || ''}"`,
        `"${ticket.service || ''}"`,
        `"${ticket.material || ''}"`,
        `"${ticket.location || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `support-tickets-dashboard-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <motion.div 
      className="flex justify-end mb-6"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.button
        onClick={exportToCSV}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-2xl flex items-center gap-2 shadow-lg font-semibold transition-all"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        📊 Export Dashboard (CSV)
      </motion.button>
    </motion.div>
  );
};

// Loading Spinner (styled like AssignTickets)
const LoadingSpinner = () => (
  <motion.div 
    className="flex justify-center items-center h-64"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.5 }}
  >
    <div className="relative">
      <motion.div
        className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full"
        aria-label="Loading spinner"
        role="status"
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
      />
      <motion.div 
        className="absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <span className="text-blue-600 font-medium">Loading dashboard...</span>
      </motion.div>
    </div>
  </motion.div>
);

// Error Component (styled like AssignTickets)
const ErrorMessage = ({ error, onRetry }) => (
  <motion.div 
    className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.5 }}
  >
    <motion.div 
      className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4"
      initial={{ scale: 0.5 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <div className="text-center">
        <motion.div
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          className="text-6xl mb-4"
        >
          ❌
        </motion.div>
        <h2 className="text-2xl font-bold text-red-600 mb-2">Error Loading Dashboard</h2>
        <p className="text-gray-700 mb-6">{error}</p>
        <motion.button
          onClick={onRetry}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-md"
        >
          🔄 Try Again
        </motion.button>
      </div>
    </motion.div>
  </motion.div>
);

// Main Dashboard Component
function Dashboard() {
  const { data, loading, error } = useDashboardData();
  const [filters, setFilters] = useState({
    dateRange: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  // Apply filters to tickets (adapted from AssignTickets logic)
  const filteredTickets = useMemo(() => {
    let filtered = [...data.tickets];

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(t => t.status?.toLowerCase() === filters.status.toLowerCase());
    }

    // Filter by date range
    if (filters.dateRange) {
      if (filters.dateRange === 'custom' && filters.startDate && filters.endDate) {
        const start = new Date(filters.startDate);
        const end = new Date(filters.endDate);
        end.setHours(23, 59, 59, 999);

        filtered = filtered.filter((t) => {
          const ticketDate = new Date(t.createdAt);
          return ticketDate >= start && ticketDate <= end;
        });
      } else if (filters.dateRange !== 'custom') {
        const range = getDateRange(filters.dateRange);
        if (range.start && range.end) {
          const start = new Date(range.start);
          const end = new Date(range.end);

          filtered = filtered.filter((t) => {
            const ticketDate = new Date(t.createdAt);
            return ticketDate >= start && ticketDate <= end;
          });
        }
      }
    }

    // Recalculate stats based on filtered tickets
    const totalTickets = filtered.length;
    const openTickets = filtered.filter(t => ['open', 'pending'].includes(t.status?.toLowerCase())).length;
    const inProgressTickets = filtered.filter(t => t.status?.toLowerCase() === 'in progress').length;
    const resolvedTickets = filtered.filter(t => t.status?.toLowerCase() === 'resolved').length;
    const closedTickets = filtered.filter(t => t.status?.toLowerCase() === 'closed').length;
    const assignedTickets = filtered.filter(t => t.isAssigned).length;
    const unassignedTickets = totalTickets - assignedTickets;
    const avgResolutionTime = (resolvedTickets + closedTickets > 0) 
      ? Math.round(
          filtered
            .filter(t => t.resolutionTime)
            .reduce((sum, t) => sum + t.resolutionTime, 0) / (resolvedTickets + closedTickets)
        ) 
      : 0;

    return {
      tickets: filtered,
      stats: { 
        ...data.stats, 
        totalTickets, 
        openTickets, 
        inProgressTickets, 
        resolvedTickets, 
        closedTickets, 
        assignedTickets, 
        unassignedTickets, 
        avgResolutionTime 
      }
    };
  }, [data.tickets, filters]);

  const handleFilterChange = (key, value) => {
    if (key === 'reset') {
      setFilters({
        dateRange: '',
        status: '',
        startDate: '',
        endDate: ''
      });
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleRetry = () => {
    window.location.reload(); // Simple retry
  };

  if (loading) return <LoadingSpinner />;

  if (error) return <ErrorMessage error={error} onRetry={handleRetry} />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <motion.div 
        className="max-w-7xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-extrabold text-gray-900 mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
            Support Tickets Dashboard
          </h1>
          <div className="flex items-center gap-4 mt-4">
            <span className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full text-sm font-semibold">
              {filteredTickets.tickets.length} Total Tickets
            </span>
            <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
              {data.stats.assignedTickets} Assigned
            </span>
            <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold">
              {data.stats.unassignedTickets} Pending
            </span>
          </div>
        </motion.div>

        {/* Filters */}
        <DashboardFilters filters={filters} onFilterChange={handleFilterChange} />

        {/* Export */}
        <ExportDashboard tickets={filteredTickets.tickets} />

        {/* Stats Cards */}
        <StatsCards stats={filteredTickets.stats} />

        {/* Charts */}
        <DashboardCharts 
          tickets={filteredTickets.tickets} 
          employees={data.employees} 
          departments={data.departments} 
        />

        {/* Recent Tickets */}
        <RecentTicketsTable tickets={filteredTickets.tickets} employees={data.employees} />

        {/* Active Filters Indicator */}
        {Object.values(filters).some(val => val !== '') && (
          <motion.div 
            className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-2xl"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-sm text-blue-800 flex items-center justify-between">
              <span>
                📊 Showing filtered results ({filteredTickets.tickets.length} tickets).{' '}
                <motion.span 
                  className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                  onClick={() => handleFilterChange('reset', null)}
                >
                  Clear filters
                </motion.span>
              </span>
              <motion.button
                onClick={() => handleFilterChange('reset', null)}
                whileHover={{ scale: 1.05 }}
                className="px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-xs font-medium"
              >
                Reset
              </motion.button>
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}

export default Dashboard;
